import { supabase } from '../lib/supabase';

// Helper function to check if error is schema-related
const isSchemaError = (error) => {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /undefined.*column/i,
      /undefined.*table/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
};

// Helper function to translate errors to Dutch
const translateError = (error) => {
  if (!error) return 'Er is een onbekende fout opgetreden';
  
  const errorMessage = error?.message || String(error);
  
  const errorMap = {
    'permission denied': 'Geen toegang tot deze vergelijking',
    'violates row-level security': 'U heeft geen rechten om deze actie uit te voeren',
    'duplicate key': 'Deze vergelijking bestaat al',
    'Network request failed': 'Netwerkfout. Controleer uw internetverbinding'
  };
  
  for (const [key, value] of Object.entries(errorMap)) {
    if (errorMessage?.includes(key)) {
      return value;
    }
  }
  
  return errorMessage || 'Er is een fout opgetreden';
};

export const comparisonService = {
  // Save a new comparison
  async saveComparison(userId, products, category = null, comparisonName = null) {
    try {
      const { data, error } = await supabase?.from('comparison_history')?.insert({
          user_id: userId,
          products: products,
          category: category,
          comparison_name: comparisonName || `Vergelijking ${new Date()?.toLocaleDateString('nl-NL')}`
        })?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        return { 
          data: null, 
          error: { 
            message: translateError(error),
            originalError: error 
          } 
        };
      }
      
      return { data, error: null };
    } catch (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      return { 
        data: null, 
        error: { 
          message: translateError(error),
          originalError: error 
        } 
      };
    }
  },

  // Get all comparisons for a user
  async getUserComparisons(userId, category = null) {
    try {
      let query = supabase?.from('comparison_history')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      
      if (category) {
        query = query?.eq('category', category);
      }
      
      const { data, error } = await query;
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        }
        return { 
          data: null, 
          error: { 
            message: translateError(error),
            originalError: error 
          } 
        };
      }
      
      return { data: data || [], error: null };
    } catch (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      return { 
        data: null, 
        error: { 
          message: translateError(error),
          originalError: error 
        } 
      };
    }
  },

  // Get a single comparison by ID
  async getComparisonById(comparisonId) {
    try {
      const { data, error } = await supabase?.from('comparison_history')?.select('*')?.eq('id', comparisonId)?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        return { 
          data: null, 
          error: { 
            message: translateError(error),
            originalError: error 
          } 
        };
      }
      
      return { data, error: null };
    } catch (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      return { 
        data: null, 
        error: { 
          message: translateError(error),
          originalError: error 
        } 
      };
    }
  },

  // Update an existing comparison
  async updateComparison(comparisonId, updates) {
    try {
      const { data, error } = await supabase?.from('comparison_history')?.update(updates)?.eq('id', comparisonId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        return { 
          data: null, 
          error: { 
            message: translateError(error),
            originalError: error 
          } 
        };
      }
      
      return { data, error: null };
    } catch (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      return { 
        data: null, 
        error: { 
          message: translateError(error),
          originalError: error 
        } 
      };
    }
  },

  // Delete a comparison
  async deleteComparison(comparisonId) {
    try {
      const { error } = await supabase?.from('comparison_history')?.delete()?.eq('id', comparisonId);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        return { 
          error: { 
            message: translateError(error),
            originalError: error 
          } 
        };
      }
      
      return { error: null };
    } catch (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      return { 
        error: { 
          message: translateError(error),
          originalError: error 
        } 
      };
    }
  }
};